import { z } from 'zod';
import {
    type NodeKind,
    nodeCfgSchema,
} from './nodes';

export const flowConnectionSchema = z.object({
    sourceNodeId: z.string(),
    sourceNodePort: z.string(),
    targetNodeId: z.string(),
    targetNodePort: z.string(),
});
export interface FlowConnection extends z.infer<typeof flowConnectionSchema> {}

/**
 * Node kinds that can self-start a control flow (e.g. by listening for external events or variable
 * changes). These are not permitted inside a privileged flow because privileged flows must have an
 * explicit, operator-triggered start so the auth context is well-defined.
 */
export const SELF_STARTING_NODE_KINDS: {
    [kind in NodeKind]: boolean } = {
    activeOperator: true,
    appletEventEmit: false,
    resetVariables: false,
    appletEventListen: true,
    attributeMaterialLink: false,
    boolean: false,
    boolCast: false,
    booleanComparison: false,
    comparisonNumber: false,
    comparisonString: false,
    createMaterial: false,
    defaultVal: false,
    eventDelay: false,
    eventOneShot: false,
    eventSwitch: false,
    eventThrottle: false,
    fileRead: false,
    fileWrite: false,
    flowError: true,
    importMaterial: false,
    materialAssembly: false,
    materialComparison: false,
    materialDisassembly: false,
    jsonAgg: false,
    jsonParse: false,
    logOutOperator: false,
    mqttPublish: false,
    mqttSubscribe: true,
    numCast: false,
    numClamp: false,
    number: false,
    performanceDataCollect: false,
    processCollect: false,
    processSolve: false,
    processStatus: false,
    script: false,
    slice: false,
    sparkplugCommander: false,
    sparkplugConsumer: true,
    start: true,
    strCast: false,
    text: false,
    timer: true,
    activatePickLight: false,
    deactivatePickLight: false,
    ioLink: true,
    addMaterialNote: false,
    isduRead: true,
    isduWrite: false,
    openProtocolConnection: true,
    pickLightChannel: true,
    tcpConnection: true,
    torqueToolCommander: false,
    torqueToolConsumer: true,
    udpConnection: true,
    txtFormat: false,
    varAssigned: true,
    varRead: false,
    varWrite: false,
    varWriteMultiple: false,
    workInstruction: true,
    workInstructionStatus: true,
    materialLookup: false,
    materialSerialNumber: false,
    workOrder: true,
    workOrderSetStatus: false,
    updateMaterialStatus: false,
    goToTab: false,
    getActiveTab: true,
    nextTab: false,
    previousTab: false,
    functionalInputs: false,
    functionalSelect: true,
    functionalOutputs: false,
    not: false,
    timestamp: false,
};

export const flowCfgSchema = z.object({
    id: z.string(),
    name: z.string(),
    nodes: z.record(nodeCfgSchema),
    connections: z.record(flowConnectionSchema),
    kind: z.union([ z.literal('regular'), z.literal('functional'), z.literal('privileged') ]).default('regular'),
    privilegedRoles: z.array(z.string()).min(1).optional(),
    comments: z.record(z.object({
        id: z.string(),
        label: z.string(),
        x: z.number(),
        y: z.number(),
        nodeLink: z.string().optional(),
    })),
    groups: z.record(z.object({
        id: z.string(),
        label: z.string(),
        nodeLinks: z.array(z.string()),
    })),
}).superRefine((flowCfg, ctx) => {
    // ensure no performance node has a connection for asset id
    for (const connId in flowCfg.connections) {
        const conn = flowCfg.connections[connId]!;
        if (conn.targetNodePort === 'assetId') {
            if (flowCfg.nodes[conn.targetNodeId]?.kind === 'performanceDataCollect') {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Performance Data Collect node does not support an asset ID connection',
                    fatal: true,
                });
            }
        }
    }

    if (flowCfg.kind === 'privileged') {
        if (!flowCfg.privilegedRoles || flowCfg.privilegedRoles.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'Privileged flows must specify at least one privilegedRole',
                fatal: true,
            });
        }
        for (const nodeId in flowCfg.nodes) {
            const node = flowCfg.nodes[nodeId]!;
            if (SELF_STARTING_NODE_KINDS[node.kind]) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Node kind "${node.kind}" is not allowed in a privileged flow because it can self-start a control flow`,
                    fatal: true,
                });
            }
        }
    }
});
